import React, { useEffect, useRef } from 'react';
import { string } from 'prop-types';

const COOKIEBOT_GROUP_ID = '842ef83c-cce8-489c-a1f4-38ed3f5017b1';
const COOKIE_DECLARATION_SRC = `https://consent.cookiebot.com/${COOKIEBOT_GROUP_ID}/cd.js`;

/**
 * Renders the Cookiebot cookie declaration (the list of cookies the site sets).
 *
 * Cookiebot's cd.js renders the declaration next to its own <script id="CookieDeclaration">
 * tag, so the script has to live where the declaration should appear. A <script> in JSX is
 * not executed by React, hence it is injected into the container after mount. On unmount
 * React removes the container together with the script and the rendered declaration, and a
 * later mount (navigating back to the page) injects a fresh script that renders it again.
 *
 * The consent banner itself is loaded separately via uc.js in public/index.html.
 *
 * Cookiebot only renders on domains registered in the domain group in the Cookiebot Manager;
 * on any other domain it shows an "is not authorized" error in place of the declaration.
 */
const CookieDeclaration = props => {
  const { className, culture } = props;
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const script = document.createElement('script');
    script.id = 'CookieDeclaration';
    script.type = 'text/javascript';
    script.async = true;
    // Without data-culture Cookiebot uses the browser language, so an English browser would
    // get an English declaration on this German page.
    script.setAttribute('data-culture', culture);
    script.src = COOKIE_DECLARATION_SRC;
    container.appendChild(script);

    return () => {
      // Drop the script and whatever Cookiebot rendered, so a re-run never stacks two declarations
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
    };
  }, [culture]);

  return <div ref={containerRef} className={className} />;
};

CookieDeclaration.defaultProps = {
  className: null,
  culture: 'DE',
};

CookieDeclaration.propTypes = {
  className: string,
  culture: string,
};

export default CookieDeclaration;
